import capitalize from 'lodash/capitalize';

// The values are defined on the backend
export enum OrderType {
  limit = 'limit',
  quantity = 'quantity',
  maxInvestment = 'max_investment',
}

// The values are defined on the backend
export enum OrderKindType {
  limit = 'limit',
  market = 'market',
}

// The values are defined on the backend
export enum ChoiceVariant {
  yes = 'yes',
  no = 'no',
}

// The values are defined on the backend.
// `market` is not in apiDoc/openapi.json, but the API does return it.
export enum MarketType {
  limit = 'limit',
  market = 'market',
  quantity = 'quantity',
  maxInvestment = 'max_investment',
}

// Backend values are snake_cased, e.g. `max_investment` -> `Max investment`
export const formatOrderEnumValue = (value: string): string =>
  value ? capitalize(value.replace(/_/g, ' ')) : '';

// The values are defined by the backend (OrderActionEnum). `buy` is an entry,
// `exit` is a sell, the rest are cancellations.
export enum OrderAction {
  buy = 'buy',
  cancelManual = 'cancel_manual',
  cancelAuto = 'cancel_auto',
  exit = 'exit',
}

export interface AutoCancel {
  orderId: string;
  timeoutAt: Date;
  status: AutoCancelStatus;
}

export interface StopLoss {
  orderId: string;
  triggerType: TriggerType;
  price: number;
  status: AutoCancelStatus;
}

export enum CancelType {
  auto = 'auto',
  manual = 'manual',
}

export interface Order {
  id: string;
  created: Date;
  userId: string;
  action: OrderAction;
  orderType: OrderType;
  choice: ChoiceVariant;
  eventId: string;
  requestedItemCount: number;
  requestedMaxInv: number;
  matchedQuantity: number;
  exitedQuantity: number;
  price: number | null;
  slippage: number | null;
  matching: MarketType | null;
  labels: OrderLabel[];
  auto_cancel: AutoCancel | null;
  stop_loss: StopLoss | null;
  take_profit: StopLoss | null;
}

// The values are defined by the backend
export enum AutoCancelStatus {
  pending = 'pending',
  settled = 'settled',
  failed = 'failed',
  canceled = 'canceled',
  voided = 'voided',
  executed = 'executed',
}

// The values are defined by the backend
export enum TriggerType {
  stop_loss = 'stop_loss',
  take_profit = 'take_profit',
}

// The values are defined by the backend (LabelsEnum)
export enum OrderLabel {
  cancelled = 'cancelled',
  voided = 'voided',
  unmatched = 'unmatched',
  matched = 'matched',
  result = 'result',
  exited = 'exited',
  exiting = 'exiting',
}

// The values are defined by the backend (OrderItemRoleEnum): acquired — owned
// by the order; held — paid for, awaiting a match; orphan — paid for but
// never matched, the hold went to an item bought off another holder;
// selling — reserved by this exit order; sold — sold by this exit order
export enum OrderItemRole {
  acquired = 'acquired',
  held = 'held',
  orphan = 'orphan',
  selling = 'selling',
  sold = 'sold',
}

export interface OrderItemOwnership {
  ownerUserId: string;
  ownerOrderId: string;
  acquired: Date;
  released: Date | null;
  releaseReason: string;
  releaseOrderId: string;
  sellerUserId: string;
  sellerOrderId: string;
  isCurrentOrder: boolean;
}

export interface OrderItem {
  id: string;
  created: Date;
  updated: Date;
  side: ChoiceVariant;
  role: OrderItemRole;
  price: number;
  real: number;
  promo: number;
  userId: string;
  counterpartySide: ChoiceVariant;
  counterpartyUserId: string;
  counterpartyOrderId: string;
  ownership: OrderItemOwnership[];
  pairItemId: string;
}

export interface OrderFullInfo {
  id: string;
  eventId: string;
  action: OrderAction;
  startDate: Date;
  endDate: Date;
  orderType: OrderKindType;
  choice: ChoiceVariant;
  requested_items_quantity: number | null;
  executed_items_quantity: number | null;
  requested_max_inv: number | null;
  executed_max_inv: number | null;
  investment: number | null;
  invested: number | null;
  current_value: number | null;
  live_profit: number | null;
  auto_cancel: AutoCancel | null;
  stop_loss: StopLoss | null;
  take_profit: StopLoss | null;
  price: number | null;
  slippage: number | null;
  selling_price: number | null;
  potential_profit: number | null;
  avg_exit_price: number | null;
  unfilled: number | null;
  matched_quantity: number | null;
  matching: MarketType | null;
  labels: OrderLabel[];
  profit: number | null;
  cancelled: CancelType | null;
}
